<template>
  <ux-panel>
    <ux-panel-header>
      <div class="font-muted font-sm">
        <flex-row gap center>
          <flex-cell> Select a location using the map below </flex-cell>
          <flex-cell shrink>
            <a @click="setToUserPosition"
              >Set to my position <ux-icon icon="fa-map-marker" right
            /></a>
          </flex-cell>
        </flex-row>
      </div>
    </ux-panel-header>
    <div class="map-outer">
      <div class="indicator" v-if="initializing"><spinner /></div>
      <div class="map-inner" ref="mapContainer"></div>
    </div>
    <ux-panel-body v-if="showFields">
      <template v-if="showTitle">
        <text-field @touched="touch" :field="titleField" v-model="title" />
      </template>
      <template v-if="showAddress">
        <text-field
          @touched="touch"
          :field="addressLine1Field"
          v-model="model.addressLine1"
        />
        <flex-row gap>
          <flex-cell>
            <text-field
              @touched="touch"
              :field="suburbField"
              v-model="model.suburb"
            />
          </flex-cell>
          <flex-cell>
            <text-field
              @touched="touch"
              :field="stateField"
              v-model="model.state"
            />
          </flex-cell>
          <flex-cell>
            <text-field
              @touched="touch"
              :field="postalCodeField"
              v-model="model.postalCode"
            />
          </flex-cell>
        </flex-row>

        <country-select
          @touched="touch"
          :field="countryField"
          v-model="model.country"
        />
      </template>
      <flex-row gap center v-if="showInputs">
        <flex-cell>
          <text-field @touched="touch" :field="latField" v-model="lat" />
        </flex-cell>
        <flex-cell>
          <text-field @touched="touch" :field="lngField" v-model="lng" />
        </flex-cell>
      </flex-row>
      <template v-if="showThreshold">
        <text-field @touched="touch" :field="thresholdField" v-model="radius" />
      </template>
    </ux-panel-body>
  </ux-panel>
</template>
<script>
import Spinner from "../../ui/spinner.vue";
import TextField from "./textfield.vue";
import CountrySelect from "./country.vue";

export default {
  components: {
    TextField,
    Spinner,
    CountrySelect,
  },
  props: {
    showTitle: {
      type: Boolean,
      default: true,
    },
    showAddress: {
      type: Boolean,
      default: true,
    },
    showInputs: {
      type: Boolean,
      default: true,
    },
    showThreshold: {
      type: Boolean,
      default: true,
    },
    modelValue: {
      type: Object,
      default() {
        return {
          latitude: 0,
          longitude: 0,
          threshold: 0,
          title: "",
          address: "",
          search: "",
        };
      },
    },
    assumeDefault: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    initializing() {
      return this.loadingUserPosition || this.loadingMapScript;
    },
    showFields() {
      return this.showInputs || this.showThreshold;
    },

    position: {
      get() {
        return this.model;
      },
      set(pos) {
        const self = this;
        self.model = {
          ...(self.model || {}),
          ...pos,
        };
        self.redrawMarker();
        self.$emit("update:modelValue", self.model);
        // self.dispatch();
      },
    },
    address: {
      get() {
        return this.model.address || "";
      },
      set(s) {
        this.model.address = s;
      },
    },
    title: {
      get() {
        return this.model.title || "";
      },
      set(s) {
        this.model.title = s;
      },
    },
    lat: {
      get() {
        return this.model?.latitude || 0;
      },
      set(v) {
        if (this.model.latitude != v) {
          this.position = {
            latitude: v,
          };
          // this.model.latitude = v;
          // this.redrawMarker();
        }
      },
    },
    radius: {
      get() {
        return isNaN(this.model?.threshold)
          ? 0
          : Number(this.model.threshold) || 0;
      },
      set(v) {
        v = Number(v);

        if (this.model.threshold != v) {
          // this.model.threshold = v;
          this.position = {
            threshold: v,
          };
          this.redrawCircle();
        }
      },
    },
    lng: {
      get() {
        return this.model?.longitude || 0;
      },
      set(v) {
        if (this.model.longitude != v) {
          this.position = {
            longitude: v,
          };
        }
      },
    },
  },
  data() {
    const latField = {
      title: "Latitude",
      key: "lat",
      type: "number",
      minimum: 0,
      maximum: 1,
    };
    const lngField = {
      title: "Longitude",
      key: "lng",
      type: "number",
      minimum: 0,
      maximum: 1,
    };

    const thresholdField = {
      title: "Radius (Meters)",
      description:
        "Enter the maximum distance in meters from this location that can be considered valid",
      key: "radius",
      type: "number",
      minimum: 0,
      maximum: 1,
    };

    const titleField = {
      title: "Title",
      description: "Optionally enter a descriptive name for this location",
      key: "title",
      type: "string",
      minimum: 0,
      maximum: 1,
    };

    const addressLine1Field = {
      title: "Address Line 1",
      key: "addressLine1",
      type: "string",
      minimum: 0,
      maximum: 1,
    };

    const addressLine2Field = {
      title: "Address Line 2",
      key: "addressLine2",
      type: "string",
      minimum: 0,
      maximum: 1,
    };

    const suburbField = {
      title: "Suburb",
      key: "suburb",
      type: "string",
      minimum: 0,
      maximum: 1,
    };

    const stateField = {
      title: "State",
      key: "state",
      type: "string",
      minimum: 0,
      maximum: 1,
    };

    const postalCodeField = {
      title: "Postal Code",
      key: "postalCode",
      type: "string",
      minimum: 0,
      maximum: 1,
    };

    const countryField = {
      title: "Country",
      key: "country",
      type: "string",
      minimum: 0,
      maximum: 1,
    };

    const startingModel = this.modelValue || {};
    startingModel.latitude = startingModel.latitude || 0;
    startingModel.longitude = startingModel.longitude || 0;
    startingModel.threshold = startingModel.threshold || 0;

    return {
      map: null,
      marker: null,
      circle: null,
      model: startingModel,
      latField,
      lngField,
      thresholdField,
      titleField,
      addressLine1Field,
      addressLine2Field,
      suburbField,
      stateField,
      postalCodeField,
      countryField,
      userPosition: null,
      loadingUserPosition: true,
      loadingMapScript: true,
      initialised: false,
    };
  },
  mounted() {
    const self = this;

    // Use the google maps api key set for the application or the built in qik.dev one as a backup
    const apiKey =
      self.$sdk.app?.global?.googleMapsAPIKey ||
      "AIzaSyDxBa2lg5dUZWvVSAO2EfgHHKuNnERMzec";

    // Ensure the maps are loaded
    self.$sdk.utils
      .loadExternalScript(
        `https://maps.googleapis.com/maps/api/js?key=${apiKey}`,
      )
      .then(
        function (res) {
          self.loadingMapScript = false;
        },
        function (err) {
          self.loadingMapScript = false;
        },
      );

    if (navigator.geolocation) {
      self.loadingUserPosition = true;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          self.userPosition = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          self.loadingUserPosition = false;
        },
        (error) => {
          console.error("Error getting user position:", error);
          self.loadingUserPosition = false;
        },
      );
    } else {
      self.loadingUserPosition = false;
    }
  },
  watch: {
    modelValue(m) {
      this.model = m;
      this.redrawMarker();
    },
    initializing: { handler: "checkInitialisation", immediate: true },
  },
  beforeUnmount() {
    if (this.map) {
      window.google.maps.event.clearInstanceListeners(this.map);
    }
  },
  methods: {
    setToUserPosition() {
      const self = this;
      if (self.userPosition?.latitude && self.userPosition?.longitude) {
        self.position = {
          latitude: self.userPosition.latitude,
          longitude: self.userPosition.longitude,
          title: "",
          address: "",
        };
      }
    },
    checkInitialisation() {
      const self = this;
      if (self.loadingUserPosition || self.loadingMapScript) {
        console.log("Still initializing...", [
          self.loadingUserPosition,
          this.loadingMapScript,
        ]);
      } else {
        // Check whether we should assume the
        // user's location by default
        if (self.assumeDefault) {
          // Check if we already have values
          const mLat = Number(self.model?.latitude || 0);
          const mLon = Number(self.model?.longitude || 0);

          // If there is no values
          if (!mLat || !mLon) {
            // Check the user's position
            const uLat = Number(self.userPosition?.latitude || 0);
            const uLon = Number(self.userPosition?.longitude || 0);

            // If we know where the user is
            // that should be our starting point
            if (uLat && uLon) {
              self.model = {
                ...self.model,
                latitude: uLat,
                longitude: uLon,
              };
            }
          }
        }

        // Initialize the map
        self.initializeMap();
      }
    },
    focus() {},
    redrawMarker() {
      const self = this;

      const mLat = Number(self.model.latitude || 0);
      const mLng = Number(self.model.longitude || 0);

      const pos = new google.maps.LatLng(mLat, mLng);
      self.marker.setPosition(pos);

      if (mLat && mLng) {
        self.marker.setVisible(true);
        self.map.setCenter(pos);
      } else {
        self.marker.setVisible(false);
      }

      self.redrawCircle();
    },
    redrawCircle() {
      const self = this;

      // If there is no threshold
      if (!self.radius) {
        // If there is a circle
        if (self.circle) {
          // Remove the circle
          self.circle.setMap(null);
          self.circle = null;
        }

        // Return early
        return;
      }

      const pos = new google.maps.LatLng(
        Number(self.model.latitude),
        Number(self.model.longitude),
      );

      // If there is a circle
      if (self.circle) {
        // Update it's position
        self.circle.setRadius(self.radius);
        self.circle.setCenter(pos);
      } else {
        // Draw a circle on the map
        self.circle = new window.google.maps.Circle({
          center: pos,
          radius: Number(self.radius), // Radius in meters
          strokeColor: "#FF0000",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: "#FF0000",
          fillOpacity: 0.35,
          map: self.map,
        });
      }
    },
    initializeMap() {
      const self = this;

      if (self.initialised) {
        return;
      }

      self.initialised = true;
      let modelLat = Number(self.model?.latitude || 0);
      let modelLng = Number(self.model?.longitude || 0);
      let userLat = Number(self.userPosition?.latitude || 0);
      let userLng = Number(self.userPosition?.longitude || 0);

      let defaultLat = modelLat || 0;
      let defaultLng = modelLng || 0;

      if (!defaultLat && !defaultLng) {
        defaultLat = userLat;
        defaultLng = userLng;
      }

      // Setup the map
      self.map = new window.google.maps.Map(self.$refs.mapContainer, {
        center: {
          lat: defaultLat,
          lng: defaultLng,
        },
        zoom: 12,
        streetViewControl: false,
      });

      self.marker = new window.google.maps.Marker({
        position: {
          lat: defaultLat,
          lng: defaultLng,
        },
        map: self.map,
        draggable: true,
      });

      if (modelLat && modelLng) {
        self.marker.setVisible(true);
      } else {
        self.marker.setVisible(false);
      }

      // Add the listeners
      self.map.addListener("click", (event) => {
        self.position = {
          latitude: event.latLng.lat(),
          longitude: event.latLng.lng(),
        };
      });

      self.marker.addListener("dragend", () => {
        self.position = {
          latitude: self.marker.getPosition().lat(),
          longitude: self.marker.getPosition().lng(),
        };
      });

      // Render the threshold circle
      self.redrawCircle();
    },
  },
};
</script>
<style lang="scss">
.map-outer {
  position: relative;
  background: color-mix(in lab, currentColor 10%, transparent);

  .map-inner {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
  }

  .indicator {
    position: absolute;
    pointer-events: none;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    text-align: center;
    width: 100%;
    z-index: 8;
    font-size: 3em;
  }
}

a {
  cursor: pointer;
}
</style>
